import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { speakersApi } from '../../api/speakers.js';
import { SearchBar } from '../../components/common/SearchBar.jsx';
import { StatusBadge } from '../../components/common/StatusBadge.jsx';
import { LoadingSpinner } from '../../components/common/LoadingSpinner.jsx';
import { EmptyState } from '../../components/common/EmptyState.jsx';
import { ErrorAlert } from '../../components/common/ErrorAlert.jsx';
import { usePageHeader } from '../../hooks/usePageHeader.jsx';
import { formatDate } from '../../utils/dates.js';
import { SPEAKER_STATUSES } from '../../utils/constants.js';

export function SpeakerListPage() {
  usePageHeader('Speakers', 'Every speaker, from a first-contact lead through active');
  const [searchParams] = useSearchParams();
  const [speakers, setSpeakers] = useState([]);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState(searchParams.get('status') ?? '');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    speakersApi.list({ status, q }).then(setSpeakers).catch(setError).finally(() => setLoading(false));
  }, [status, q]);

  const filtered = speakers.filter((s) => {
    if (!q) return true;
    const search = q.toLowerCase();
    return (
      `${s.firstName} ${s.lastName}`.toLowerCase().includes(search) ||
      s.organization?.toLowerCase().includes(search) ||
      s.email?.toLowerCase().includes(search) ||
      s.topic?.toLowerCase().includes(search)
    );
  });

  return (
    <div>
      <div className="page-toolbar">
        <SearchBar value={q} onChange={setQ} placeholder="Search speakers…" />
        <div className="inline">
          <select
            className="form-select"
            style={{ width: 'auto' }}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            aria-label="Filter by status"
          >
            <option value="">All</option>
            {SPEAKER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <Link to="/speakers/new" className="btn btn-primary">+ New speaker</Link>
        </div>
      </div>
      <div className="page-body">
        <ErrorAlert error={error} />
        {loading ? <LoadingSpinner /> : filtered.length === 0 ? (
          <EmptyState
            title="No speakers found"
            description={q ? 'Try a different search.' : 'Add your first speaker profile.'}
            action={<Link to="/speakers/new" className="btn btn-primary">Add speaker</Link>}
          />
        ) : (
          <div className="data-table" style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Organization</th>
                  <th>Email</th>
                  <th>Topic / topic areas</th>
                  <th>Status</th>
                  <th>Next follow-up</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => (
                  <tr key={s.id}>
                    <td>
                      <Link to={`/speakers/${s.id}`}>
                        {s.firstName} {s.lastName}
                        {s.preferredName && s.preferredName !== s.firstName && (
                          <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-xs)', marginLeft: 4 }}>
                            ({s.preferredName})
                          </span>
                        )}
                      </Link>
                    </td>
                    <td>{s.organization ?? '—'}</td>
                    <td><a href={`mailto:${s.email}`}>{s.email}</a></td>
                    <td style={{ fontSize: 'var(--text-xs)' }}>{(s.topicAreas ?? []).join(', ') || s.topic || '—'}</td>
                    <td><StatusBadge status={s.status} type="speaker" /></td>
                    <td style={{ fontSize: 'var(--text-sm)', color: s.nextFollowUpDate && s.nextFollowUpDate < new Date().toISOString().slice(0, 10) ? 'var(--color-danger)' : undefined }}>
                      {formatDate(s.nextFollowUpDate)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
